import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,

} from '@mui/icons-material';
import { InventoryItem, InventoryMovement } from '../types';
import inventoryService from '../services/inventoryService';

interface InventoryDetailModalProps {
  open: boolean;
  onClose: () => void;
  sku: string | null;
}

interface EditableFields {
  QuantityOnHand: number;
  ReorderLevel: number;
  Cost: number;
  RetailPrice: number;
  Location: string;
  Description: string;
}

export const InventoryDetailModal: React.FC<InventoryDetailModalProps> = ({
  open,
  onClose,
  sku
}) => {
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedFields, setEditedFields] = useState<EditableFields | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadItemDetails = useCallback(async () => {
    if (!sku) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const [itemData, movementsData] = await Promise.all([
        inventoryService.getInventoryItem(sku),
        inventoryService.getInventoryMovements(sku, 50)
      ]);
      
      setItem(itemData);
      setMovements(movementsData);
      setEditedFields({
        QuantityOnHand: itemData.QuantityOnHand,
        ReorderLevel: itemData.ReorderLevel || 0,
        Cost: itemData.Cost || 0,
        RetailPrice: itemData.RetailPrice || 0,
        Location: itemData.Location || '',
        Description: itemData.Description || ''
      });
    } catch (error) {
      console.error('Error loading item details:', error);
      setError('Failed to load item details');
    } finally {
      setLoading(false);
    }
  }, [sku]);

  useEffect(() => {
    if (open && sku) {
      loadItemDetails();
    }
  }, [open, sku, loadItemDetails]);

  const handleStartEdit = () => {
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    if (editedFields && item) {
      setEditedFields({
        QuantityOnHand: item.QuantityOnHand,
        ReorderLevel: item.ReorderLevel || 0,
        Cost: item.Cost || 0,
        RetailPrice: item.RetailPrice || 0,
        Location: item.Location || '',
        Description: item.Description || ''
      });
    }
  };

  const handleSaveEdit = async () => {
    if (!item || !editedFields) return;
    
    try {
      setLoading(true);
      
      const updates = {
        QuantityOnHand: editedFields.QuantityOnHand,
        ReorderLevel: editedFields.ReorderLevel,
        Cost: editedFields.Cost,
        RetailPrice: editedFields.RetailPrice,
        Location: editedFields.Location,
        Description: editedFields.Description
      };
      
      const updatedItem = await inventoryService.updateInventoryItem(item.SKU, updates);
      setItem(updatedItem);
      setEditing(false);
      setSuccess('Item updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error updating item:', error);
      setError('Failed to update item');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: keyof EditableFields, value: any) => {
    if (!editedFields) return;
    setEditedFields(prev => prev ? { ...prev, [field]: value } : null);
  };

  const formatCurrency = (value?: number) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const getStockStatus = (item: InventoryItem) => {
    if (item.QuantityOnHand === 0) return { label: 'Out of Stock', color: 'error' as const };
    if (item.QuantityOnHand <= (item.ReorderLevel || 10)) return { label: 'Low Stock', color: 'warning' as const };
    if (item.QuantityOnHand > (item.ReorderLevel || 10) * 3) return { label: 'Overstock', color: 'info' as const };
    return { label: 'In Stock', color: 'success' as const };
  };

  const calculateDaysOfSupply = (item: InventoryItem) => {
    if (!item.TurnoverRate || item.TurnoverRate === 0) return 'N/A';
    return Math.round(365 / item.TurnoverRate);
  };

  if (!item) {
    return null;
  }

  const status = getStockStatus(item);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {item.Product} ({item.SKU})
            </Typography>
            <Box>
              {editing ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveEdit}
                    disabled={loading}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEdit}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </Box>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleStartEdit}
                >
                  Edit
                </Button>
              )}
              <IconButton onClick={onClose} sx={{ ml: 1 }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Basic Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">SKU</Typography>
                        <Typography variant="body1">{item.SKU}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Brand</Typography>
                        <Typography variant="body1">{item.Brand || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Description</Typography>
                        {editing ? (
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={editedFields?.Description || ''}
                            onChange={(e) => handleFieldChange('Description', e.target.value)}
                            size="small"
                          />
                        ) : (
                          <Typography variant="body1">{item.Description || 'No description available'}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Category</Typography>
                        <Typography variant="body1">{item.Category || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Product Type</Typography>
                        <Typography variant="body1">{item.ProductType || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {item.Hazmat && (
                            <Chip 
                              icon={<WarningIcon />} 
                              label="Hazmat" 
                              color="error" 
                              size="small"
                            />
                          )}
                          <Chip 
                            label={status.label} 
                            color={status.color} 
                            size="small"
                          />
                          {item.IsDiscontinued && (
                            <Chip label="Discontinued" color="default" size="small" />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Stock & Pricing */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Stock & Pricing
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Quantity on Hand</Typography>
                        {editing ? (
                          <TextField
                            fullWidth
                            type="number"
                            value={editedFields?.QuantityOnHand || 0}
                            onChange={(e) => handleFieldChange('QuantityOnHand', Number(e.target.value))}
                            size="small"
                          />
                        ) : (
                          <Typography variant="h6" color="primary">
                            {item.QuantityOnHand}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Available</Typography>
                        <Typography variant="h6" color="secondary">
                          {item.QuantityAvailable || item.QuantityOnHand}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Reorder Level</Typography>
                        {editing ? (
                          <TextField
                            fullWidth
                            type="number"
                            value={editedFields?.ReorderLevel || 0}
                            onChange={(e) => handleFieldChange('ReorderLevel', Number(e.target.value))}
                            size="small"
                          />
                        ) : (
                          <Typography variant="body1">{item.ReorderLevel || '-'}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Reorder Quantity</Typography>
                        <Typography variant="body1">{item.ReorderQuantity || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Cost</Typography>
                        {editing ? (
                          <TextField
                            fullWidth
                            type="number"
                            value={editedFields?.Cost || 0}
                            onChange={(e) => handleFieldChange('Cost', Number(e.target.value))}
                            size="small"
                            inputProps={{ step: 0.01 }}
                          />
                        ) : (
                          <Typography variant="body1">{formatCurrency(item.Cost)}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Retail Price</Typography>
                        {editing ? (
                          <TextField
                            fullWidth
                            type="number"
                            value={editedFields?.RetailPrice || 0}
                            onChange={(e) => handleFieldChange('RetailPrice', Number(e.target.value))}
                            size="small"
                            inputProps={{ step: 0.01 }}
                          />
                        ) : (
                          <Typography variant="body1">{formatCurrency(item.RetailPrice)}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Total Value</Typography>
                        <Typography variant="h6" color="success.main">
                          {formatCurrency((item.Cost || 0) * item.QuantityOnHand)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Location</Typography>
                        {editing ? (
                          <TextField
                            fullWidth
                            value={editedFields?.Location || ''}
                            onChange={(e) => handleFieldChange('Location', e.target.value)}
                            size="small"
                          />
                        ) : (
                          <Typography variant="body1">{item.Location || '-'}</Typography>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Analytics & Performance */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Analytics & Performance
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Turnover Rate</Typography>
                        <Typography variant="body1">
                          {item.TurnoverRate ? `${(item.TurnoverRate * 100).toFixed(1)}%` : 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Days of Supply</Typography>
                        <Typography variant="body1">
                          {calculateDaysOfSupply(item)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Last Sold</Typography>
                        <Typography variant="body1">
                          {formatDate(item.LastSoldDate)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Last Received</Typography>
                        <Typography variant="body1">
                          {formatDate(item.LastReceivedDate)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Unit Weight</Typography>
                        <Typography variant="body1">
                          {item.UnitWeight ? `${item.UnitWeight} lbs` : '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Expiration Date</Typography>
                        <Typography variant="body1">
                          {formatDate(item.ExpirationDate)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Supplier Information */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Supplier Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Supplier ID</Typography>
                        <Typography variant="body1">{item.SupplierID || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Supplier SKU</Typography>
                        <Typography variant="body1">{item.SupplierSKU || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Lead Time</Typography>
                        <Typography variant="body1">
                          {item.LeadTimeDays ? `${item.LeadTimeDays} days` : '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Sale Price</Typography>
                        <Typography variant="body1">{formatCurrency(item.SalePrice)}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Movement History */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recent Movement History
                    </Typography>
                    {movements.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell align="right">Quantity</TableCell>
                              <TableCell>Reason</TableCell>
                              <TableCell>Order #</TableCell>
                              <TableCell>Notes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {movements.slice(0, 10).map((movement) => (
                              <TableRow key={movement.MovementID}>
                                <TableCell>{formatDate(movement.Date)}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={movement.MovementType} 
                                    size="small"
                                    color={movement.MovementType === 'IN' ? 'success' : 'error'}
                                  />
                                </TableCell>
                                <TableCell align="right">{movement.Quantity}</TableCell>
                                <TableCell>{movement.Reason || '-'}</TableCell>
                                <TableCell>{movement.OrderNumber || '-'}</TableCell>
                                <TableCell>{movement.Notes || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary">No movement history available</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Error and Success Messages */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert onClose={() => setSuccess(null)} severity="success">{success}</Alert>
      </Snackbar>
    </>
  );
};
